import React, { useState, useEffect, useMemo, useContext, createContext } from 'react';
import {
    Table, Button, Modal, Form, Input, InputNumber,
    Switch, Space, message, Popconfirm, Spin, Tag
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SubnodeOutlined, RightOutlined, DownOutlined, HolderOutlined } from '@ant-design/icons';
import { motion, AnimatePresence } from 'framer-motion';
import { apiClient } from '../../services/agentService';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers' ;

const { TextArea } = Input;

// --- Top-level ID context (child satirlarda DnD devre disi birakmak icin) ---
const TopLevelIdsContext = createContext([]);

// --- Draggable Row (sadece top-level satirlar icin) ---
const DraggableRow = (props) => {
    const id = props['data-row-key'];
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });

    const style = {
        ...props.style,
        transform: CSS.Translate.toString(transform),
        transition,
        ...(isDragging ? { position: 'relative', zIndex: 9999, opacity: 0.85 } : {}),
    };

    return <tr {...props} ref={setNodeRef} style={style} {...attributes} {...listeners} />;
};

// --- Table Row wrapper: top-level → DraggableRow, child → plain <tr> ---
const TableRow = (props) => {
    const topLevelIds = useContext(TopLevelIdsContext);
    const id = props['data-row-key'];

    if (topLevelIds.includes(id)) {
        return <DraggableRow {...props} />;
    }
    return <tr {...props} />;
};

const ChatOptionsPage = () => {
    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [editingOption, setEditingOption] = useState(null);
    const [parentForChild, setParentForChild] = useState(null);
    const [expandedKeys, setExpandedKeys] = useState([]);
    const [form] = Form.useForm();
    const useLlmValue = Form.useWatch('useLlm', form);

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
        useSensor(KeyboardSensor)
    );

    const fetchOptions = async (showLoading = false) => {
        if (showLoading) setLoading(true);
        try {
            const response = await apiClient.get('/chat-options');
            setOptions(response.data || []);
        } catch (err) {
            message.error('Seçenekler yüklenemedi');
        } finally {
            if (showLoading) setLoading(false);
        }
    };

    useEffect(() => {
        fetchOptions(true);
    }, []);

    // --- Tree depth hesaplama (max 3 seviye: 0, 1, 2) ---
    const MAX_DEPTH = 5;

    const { depthMap, breadcrumbMap } = useMemo(() => {
        const dMap = {};
        const bMap = {};
        const walk = (items, depth, parentPath) => {
            (items || []).forEach(item => {
                dMap[item.id] = depth;
                bMap[item.id] = parentPath;
                if (item.children?.length) walk(item.children, depth + 1, [...parentPath, item.displayText]);
            });
        };
        walk(options, 0, []);
        return { depthMap: dMap, breadcrumbMap: bMap };
    }, [options]);

    // --- Top-level ID listesi (DnD icin) ---
    const topLevelIds = useMemo(() => (options || []).map(opt => opt.id), [options]);

    // --- Drag-and-drop handler ---
    const handleDragEnd = async (event) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;

        // Sadece top-level siralama
        const oldIndex = options.findIndex(opt => opt.id === active.id);
        const newIndex = options.findIndex(opt => opt.id === over.id);

        if (oldIndex === -1 || newIndex === -1) return;

        // Optimistic update — UI'da hemen guncelle
        const newOptions = arrayMove(options, oldIndex, newIndex);
        setOptions(newOptions);

        // Backend'e yeni siralari gonder
        try {
            const updates = newOptions.map((opt, index) => ({
                id: opt.id,
                newOrder: index + 1,
            }));

            // Sadece sirasi degisenleri guncelle
            await Promise.all(
                updates
                    .filter(u => {
                        const original = options.find(o => o.id === u.id);
                        return original && original.displayOrder !== u.newOrder;
                    })
                    .map(u =>
                        apiClient.put(`/chat-options/${u.id}`, {
                            ...options.find(o => o.id === u.id),
                            displayOrder: u.newOrder,
                            parentId: options.find(o => o.id === u.id)?.parentId || null,
                        })
                    )
            );

            message.success('Sıralama güncellendi');
            fetchOptions();
        } catch (err) {
            message.error('Sıralama güncellenemedi');
            fetchOptions(true); // Hata durumunda backend'den taze veri al
        }
    };

    // --- Ust secenek CRUD ---

    const handleAdd = () => {
        setEditingOption(null);
        setParentForChild(null);
        form.resetFields();
        form.setFieldsValue({ isActive: true, useLlm: false, displayOrder: 1 });
        setModalVisible(true);
    };

    const handleEdit = (record) => {
        setEditingOption(record);
        setParentForChild(record.parentId ? { id: record.parentId } : null);
        form.setFieldsValue(record);
        setModalVisible(true);
    };

    const handleDelete = async (id) => {
        try {
            await apiClient.delete(`/chat-options/${id}`);
            message.success('Seçenek silindi');
            fetchOptions();
        } catch (err) {
            message.error('Silme hatası');
        }
    };

    // --- Alt secenek CRUD ---

    const handleAddChild = (parentRecord) => {
        setEditingOption(null);
        setParentForChild(parentRecord);
        form.resetFields();
        form.setFieldsValue({ isActive: true, useLlm: false, displayOrder: 1 });
        setModalVisible(true);
    };

    // --- Ortak submit ---

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();

            if (parentForChild) {
                values.parentId = parentForChild.id;
            }

            if (editingOption) {
                await apiClient.put(`/chat-options/${editingOption.id}`, values);
                message.success('Seçenek güncellendi');
            } else {
                await apiClient.post('/chat-options', values);
                message.success('Seçenek eklendi');
            }
            setModalVisible(false);
            setParentForChild(null);
            fetchOptions();
        } catch (err) {
            if (err.errorFields) return;
            message.error('İşlem hatası');
        }
    };

    // --- Tablo kolonlari ---

    const columns = [
        {
            title: '',
            key: 'drag',
            width: 40,
            render: () => (
                <HolderOutlined style={{ cursor: 'grab', color: 'var(--admin-text-secondary)', fontSize: 16 }} />
            ),
        },
        {
            title: 'Sıra',
            dataIndex: 'displayOrder',
            key: 'displayOrder',
            width: 70,
            render: (order) => (
                <span style={{ color: 'var(--admin-text-secondary)', fontWeight: 500 }}>
                    #{order}
                </span>
            ),
        },
        {
            title: 'Emoji',
            dataIndex: 'emoji',
            key: 'emoji',
            width: 80,
            align: 'center',
            render: (emoji) => <span style={{ fontSize: 24 }}>{emoji}</span>,
        },
        {
            title: 'Tip',
            dataIndex: 'optionType',
            key: 'optionType',
            width: 180,
            render: (type) => <span className="admin-tag admin-tag-primary admin-tag-truncate" title={type}>{type}</span>,
        },
        {
            title: 'Buton Metni',
            dataIndex: 'displayText',
            key: 'displayText',
            render: (text, record) => {
                const path = breadcrumbMap[record.id] || [];
                return (
                    <span style={{ color: 'var(--admin-text-primary)', fontWeight: 500 }}>
                        {path.length > 0 && (
                            <span className="tree-breadcrumb">
                                {path.join(' › ')}{' › '}
                            </span>
                        )}
                        {text}
                        {record.children?.length > 0 && (
                            <Tag
                                color={(depthMap[record.id] ?? 0) >= 1 ? 'purple' : 'cyan'}
                                style={{ marginLeft: 8, fontSize: 11 }}
                            >
                                {record.children.length} alt seçenek
                            </Tag>
                        )}
                    </span>
                );
            },
        },
        {
            title: 'Cevap',
            dataIndex: 'useLlm',
            key: 'useLlm',
            width: 80,
            align: 'center',
            render: (useLlm) => (
                <Tag color={useLlm ? 'blue' : 'default'}>
                    {useLlm ? 'LLM' : 'Statik'}
                </Tag>
            ),
        },
        {
            title: 'Bot Cevabı',
            dataIndex: 'responseText',
            key: 'responseText',
            ellipsis: true,
            render: (text, record) => (
                <span style={{ color: 'var(--admin-text-secondary)' }}>
                    {record.useLlm ? <em style={{ opacity: 0.6 }}>LLM tarafından üretilecek</em> : text}
                </span>
            ),
        },
        {
            title: 'Durum',
            dataIndex: 'isActive',
            key: 'isActive',
            width: 100,
            render: (active) => (
                <span className={`admin-tag ${active ? 'admin-tag-success' : 'admin-tag-danger'}`}>
                    {active ? 'Aktif' : 'Pasif'}
                </span>
            ),
        },
        {
            title: 'İşlemler',
            key: 'actions',
            fixed: 'right',
            width: 190,
            render: (_, record) => {
                const depth = depthMap[record.id] ?? 0;
                const hasChildren = record.children?.length > 0;
                const isExpanded = expandedKeys.includes(record.id);
                return (
                    <Space size={8}>
                        {hasChildren && (
                            <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
                                <Button
                                    className="admin-btn-icon"
                                    icon={isExpanded ? <DownOutlined /> : <RightOutlined />}
                                    onClick={() => {
                                        setExpandedKeys(prev =>
                                            isExpanded
                                                ? prev.filter(key => key !== record.id)
                                                : [...prev, record.id]
                                        );
                                    }}
                                    title={isExpanded ? 'Alt seçenekleri gizle' : 'Alt seçenekleri göster'}
                                />
                            </motion.div>
                        )}
                        <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
                            <Button
                                className="admin-btn-icon"
                                icon={<EditOutlined />}
                                onClick={() => handleEdit(record)}
                            />
                        </motion.div>
                        <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
                            <Button
                                className="admin-btn-icon"
                                icon={<SubnodeOutlined />}
                                onClick={() => handleAddChild(record)}
                                disabled={depth >= MAX_DEPTH - 1}
                                title={depth >= MAX_DEPTH - 1 ? 'Maksimum derinliğe ulaşıldı' : 'Alt seçenek ekle'}
                            />
                        </motion.div>
                        <Popconfirm
                            title="Bu seçeneği silmek istediğinize emin misiniz?"
                            onConfirm={() => handleDelete(record.id)}
                            okText="Evet"
                            cancelText="Hayır"
                            okButtonProps={{ danger: true }}
                        >
                            <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
                                <Button
                                    className="admin-btn-icon danger"
                                    icon={<DeleteOutlined />}
                                />
                            </motion.div>
                        </Popconfirm>
                    </Space>
                );
            },
        },
    ];

    // Modal basligi
    const getModalTitle = () => {
        if (editingOption) return 'Seçeneği Düzenle';
        if (parentForChild) return `Alt Seçenek Ekle (${parentForChild.displayText})`;
        return 'Yeni Seçenek Ekle';
    };

    return (
        <div>
            {/* Page Header */}
            <div className="admin-page-header">
                <div>
                    <h1 className="admin-page-title">Sohbet Seçenekleri</h1>
                    <p className="admin-page-subtitle">
                        Chatbot seçeneklerini yönetin. Toplam {options.length} seçenek mevcut.
                        Sıralama için satırları sürükleyin.
                    </p>
                </div>
                <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                    <Button
                        className="admin-btn-primary"
                        icon={<PlusOutlined />}
                        onClick={handleAdd}
                    >
                        Yeni Seçenek
                    </Button>
                </motion.div>
            </div>

            {/* Table with DnD */}
            <motion.div
                className="admin-table-container"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
            >
                {loading ? (
                    <div className="admin-loading">
                        <Spin size="large" />
                    </div>
                ) : (
                    <TopLevelIdsContext.Provider value={topLevelIds}>
                    <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        modifiers={[restrictToVerticalAxis]}
                        onDragEnd={handleDragEnd}
                    >
                        <SortableContext items={topLevelIds} strategy={verticalListSortingStrategy}>
                            <Table
                                scroll={{ x: 'max-content' }}
                                columns={columns}
                                dataSource={options}
                                rowKey="id"
                                pagination={false}
                                rowClassName={(record) => `tree-depth-${depthMap[record.id] ?? 0}`}
                                components={{
                                    body: {
                                        row: TableRow,
                                    },
                                }}
                                expandable={{
                                    expandedRowKeys: expandedKeys,
                                    expandIcon: () => null,
                                    indentSize: 0,
                                }}
                            />
                        </SortableContext>
                    </DndContext>
                    </TopLevelIdsContext.Provider>
                )}
            </motion.div>

            {/* Modal */}
            <AnimatePresence>
                {modalVisible && (
                    <Modal
                        title={
                            <div className="admin-modal-heading">
                                <div className="admin-modal-heading-icon">
                                    {editingOption ? <EditOutlined /> : parentForChild ? <SubnodeOutlined /> : <PlusOutlined />}
                                </div>
                                <div>
                                    <div className="admin-modal-heading-title">{getModalTitle()}</div>
                                    <div className="admin-modal-heading-subtitle">
                                        Widget'ta gösterilecek seçeneği yapılandırın
                                    </div>
                                </div>
                            </div>
                        }
                        open={modalVisible}
                        onOk={handleSubmit}
                        onCancel={() => {
                            setModalVisible(false);
                            setParentForChild(null);
                        }}
                        okText={editingOption ? 'Güncelle' : 'Ekle'}
                        cancelText="İptal"
                        width="92vw"
                        style={{ maxWidth: 600 }}
                        className="admin-modal"
                        rootClassName="admin-modal"
                        okButtonProps={{ className: 'admin-btn-primary' }}
                        cancelButtonProps={{ className: 'admin-btn-secondary' }}
                    >
                        <Form form={form} layout="vertical" style={{ marginTop: 20 }}>
                            <Form.Item
                                name="optionType"
                                label="Seçenek Tipi"
                                rules={[{ required: true, message: 'Seçenek tipi gerekli' }]}
                            >
                                <Input placeholder="Örnek: CAMPAIGN_FLIGHT" />
                            </Form.Item>

                            <Form.Item
                                name="displayText"
                                label="Buton Metni"
                                rules={[{ required: true, message: 'Buton metni gerekli' }]}
                            >
                                <Input placeholder="Kullanıcıya gösterilecek metin" />
                            </Form.Item>

                            <Form.Item
                                name="responseText"
                                label="Bot Cevabı"
                                rules={[{ required: !useLlmValue, message: 'Bot cevabı gerekli' }]}
                            >
                                <TextArea
                                    rows={3}
                                    placeholder={useLlmValue ? 'LLM tarafından üretilecek' : 'Seçildiğinde bot\'un vereceği cevap'}
                                    disabled={useLlmValue}
                                />
                            </Form.Item>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                                <Form.Item name="redirectUrl" label="Yönlendirme URL">
                                    <Input placeholder="https://example.com/kampanya (opsiyonel)" />
                                </Form.Item>

                                <Form.Item name="emoji" label="Emoji">
                                    <Input placeholder="Örnek: ✈️" />
                                </Form.Item>
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
                                <Form.Item
                                    name="displayOrder"
                                    label="Gösterim Sırası"
                                    rules={[{ required: true, message: 'Sıra gerekli' }]}
                                >
                                    <InputNumber min={1} max={99} style={{ width: '100%' }} />
                                </Form.Item>

                                <Form.Item
                                    name="isActive"
                                    label="Durum"
                                    valuePropName="checked"
                                >
                                    <Switch
                                        checkedChildren="Aktif"
                                        unCheckedChildren="Pasif"
                                    />
                                </Form.Item>

                                <Form.Item
                                    name="useLlm"
                                    label="LLM Cevabı"
                                    valuePropName="checked"
                                >
                                    <Switch
                                        checkedChildren="LLM"
                                        unCheckedChildren="Statik"
                                    />
                                </Form.Item>
                            </div>
                        </Form>
                    </Modal>
                )}
            </AnimatePresence>
        </div>
    );
};

export default ChatOptionsPage;
